import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { getEnvironment } from '../../environments/environment';
import { ConnectionService, HasuraRole } from './connection.service';
import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    /**
     * Root URL of the backend
     */
    private rootURL = getEnvironment().apiRootURL;

    /**
     * Keycloak
     */
    private readonly keycloak = inject(Keycloak);

    constructor(private http: HttpClient, private connection: ConnectionService) { }

    /**
     * Sync OIDC user with hasura
     * @returns True if sync was successful
     */
    async sync(): Promise<boolean> {
        const result = await axios.get(`${this.rootURL}/auth/sync`, {
            headers: {
                Authorization: `Bearer ${this.keycloak.token}`,
            },
        });
        return result.status === 200;
    }

    /**
     * Attempt to upload a file by sending it to the backend
     * @param file File to upload
     * @param progress Optionally provide a BehaviorSubject that can track the upload progress
     * @returns Axios response including id of uploaded file
     */
    uploadFile(file: File, progress?: BehaviorSubject<AxiosProgressEvent | undefined>): Promise<AxiosResponse<{ file_id: string; }>> {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(
            `${this.rootURL}/storage`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${this.keycloak.token}`,
                    'x-hasura-role': this.connection.role.value,
                },
                onUploadProgress: (progressUpdate): void => {
                    if (progress) {
                        progress.next(progressUpdate);
                        if (progressUpdate.progress === 1) {
                            progress.complete();
                        }
                    }
                },
            },
        );
    }

    /**
     * Attempt to download a file from the backend
     * @param id Id of file
     * @returns Axios response with data in Blob format
     */
    retrieveFile(id: string): Promise<AxiosResponse<Blob>> {
        return axios.get(`${this.rootURL}/storage/${id}`, {
            responseType: 'blob' as 'json',
            headers: {
                'Authorization': `Bearer ${this.keycloak.token}`,
                'x-hasura-role': this.connection.role.value,
            },
        });
    }

    /**
     * Helper function to download a file
     * @param id Id of file
     * @param name Name of file
     */
    async downloadFile(id: string, name: string): Promise<void> {
        const result = await this.retrieveFile(id);
        // Temporarily add an <a> element and click it to download the file
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(result.data);
        downloadLink.setAttribute('download', name);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
    }

    /**
     * Attempt to delete a file by sending a request to the backend
     * @param id Id of file
     * @returns Axios response (without any data)
     */
    deleteFile(id: string): Promise<AxiosResponse> {
        return axios.delete(`${this.rootURL}/storage/${id}`, {
            headers: {
                'Authorization': `Bearer ${this.keycloak.token}`,
                'x-hasura-role': this.connection.role.value,
            },
        });
    }

    /**
     * Call endpoint of the backend to add a new user to keycloak
     * @param firstName First name
     * @param lastName Last nanme
     * @param email Email
     * @param role Hasura role
     * @param password Temporary password
     * @param organizationId Id of the hasura organization to add the new user to
     * @returns Object with id of created user
     */
    addUserToKeycloak(
        firstName: string,
        lastName: string,
        email: string,
        role: HasuraRole,
        password: string,
        organizationId: string,
    ): Promise<{ id: string; }> {
        return firstValueFrom(this.http.post<{ id: string; }>(
            `${this.rootURL}/users`,
            {
                firstName, lastName, email, role, password, organizationId,
            },
            { headers: { 'Authorization': `Bearer ${this.keycloak.token}`, 'x-hasura-role': this.connection.role.value! } },
        ));
    }

    /**
     * Call endpoint of the backend to set status (disable/enable) of a keycloak user
     * @param userId Id of user to set status for
     * @param enabled New status
     * @returns Void
     */
    setUserKeycloakStatus(userId: string, enabled: boolean): Promise<void> {
        return firstValueFrom(this.http.patch<any>(
            `${this.rootURL}/users/${userId}/status`,
            { userId, enabled },
            { headers: { 'Authorization': `Bearer ${this.keycloak.token}`, 'x-hasura-role': this.connection.role.value! } },
        ));
    }

    /**
     * Call endpoint of the backend to update a user. Optionally provide a password to reset the current password.
     * @param userId Id of user
     * @param firstName First name
     * @param lastName Last name
     * @param role Hasura role
     * @param password Temporary password
     * @returns Void
     */
    updateKeycloakUser(userId: string, firstName: string, lastName: string, role: HasuraRole, password?: string): Promise<void> {
        return firstValueFrom(this.http.put<any>(
            `${this.rootURL}/users/${userId}`,
            {
                userId, firstName, lastName, role, password,
            },
            { headers: { 'Authorization': `Bearer ${this.keycloak.token}`, 'x-hasura-role': this.connection.role.value! } },
        ));
    }
}
